(() => {
  function loadImage(src) {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error(`Bild konnte nicht geladen werden: ${src}`));
      img.src = src;
    });
  }

  function createMirroredCanvas(sourceCanvas) {
    const mirroredCanvas = document.createElement('canvas');
    mirroredCanvas.width = sourceCanvas.width;
    mirroredCanvas.height = sourceCanvas.height;

    const mctx = mirroredCanvas.getContext('2d');
    mctx.translate(mirroredCanvas.width, 0);
    mctx.scale(-1, 1);
    mctx.drawImage(sourceCanvas, 0, 0);

    return mirroredCanvas;
  }

  function getTrimmedDesignCanvas(designImage, trimOptions) {
    if (!trimOptions || !trimOptions.enabled) {
      return designImage;
    }

    const analysisCanvas = document.createElement('canvas');
    analysisCanvas.width = designImage.width;
    analysisCanvas.height = designImage.height;

    const actx = analysisCanvas.getContext('2d', { willReadFrequently: true });
    actx.drawImage(designImage, 0, 0);

    let imageData;
    try {
      imageData = actx.getImageData(0, 0, analysisCanvas.width, analysisCanvas.height).data;
    } catch (error) {
      console.warn('Trim deaktiviert: Kein Pixelzugriff auf das Designbild moeglich.', error);
      return designImage;
    }

    const threshold = trimOptions.alphaThreshold ?? 1;
    const padding = trimOptions.padding ?? 0;

    let minX = analysisCanvas.width;
    let minY = analysisCanvas.height;
    let maxX = -1;
    let maxY = -1;

    for (let y = 0; y < analysisCanvas.height; y++) {
      for (let x = 0; x < analysisCanvas.width; x++) {
        const alpha = imageData[(y * analysisCanvas.width + x) * 4 + 3];
        if (alpha >= threshold) {
          if (x < minX) minX = x;
          if (x > maxX) maxX = x;
          if (y < minY) minY = y;
          if (y > maxY) maxY = y;
        }
      }
    }

    if (maxX < minX || maxY < minY) {
      return designImage;
    }

    minX = Math.max(0, minX - padding);
    minY = Math.max(0, minY - padding);
    maxX = Math.min(analysisCanvas.width - 1, maxX + padding);
    maxY = Math.min(analysisCanvas.height - 1, maxY + padding);

    const trimWidth = maxX - minX + 1;
    const trimHeight = maxY - minY + 1;

    const trimmedCanvas = document.createElement('canvas');
    trimmedCanvas.width = trimWidth;
    trimmedCanvas.height = trimHeight;

    const tctx = trimmedCanvas.getContext('2d');
    tctx.drawImage(
      analysisCanvas,
      minX,
      minY,
      trimWidth,
      trimHeight,
      0,
      0,
      trimWidth,
      trimHeight
    );

    return trimmedCanvas;
  }

  function cubicBezierPoint(p0, p1, p2, p3, t) {
    const mt = 1 - t;
    const mt2 = mt * mt;
    const t2 = t * t;

    return {
      x: mt2 * mt * p0.x + 3 * mt2 * t * p1.x + 3 * mt * t2 * p2.x + t2 * t * p3.x,
      y: mt2 * mt * p0.y + 3 * mt2 * t * p1.y + 3 * mt * t2 * p2.y + t2 * t * p3.y
    };
  }

  function lerpPoint(a, b, t) {
    return {
      x: a.x + (b.x - a.x) * t,
      y: a.y + (b.y - a.y) * t
    };
  }

  function ensureFinalWarpHandles(finalWarp) {
    if (finalWarp.handles) {
      return;
    }

    finalWarp.handles = {
      topLeft: lerpPoint(finalWarp.topLeft, finalWarp.topRight, 1 / 3),
      topRight: lerpPoint(finalWarp.topLeft, finalWarp.topRight, 2 / 3),
      bottomRight: lerpPoint(finalWarp.bottomLeft, finalWarp.bottomRight, 2 / 3),
      bottomLeft: lerpPoint(finalWarp.bottomLeft, finalWarp.bottomRight, 1 / 3)
    };
  }

  function drawImageStripToQuad(ctx, img, sx, sy, sw, sh, p1, p2, p3, p4, overlap = 1) {
    const dx1 = p2.x - p1.x;
    const dy1 = p2.y - p1.y;
    const dx2 = p4.x - p1.x;
    const dy2 = p4.y - p1.y;

    ctx.save();

    const ex = overlap * 0.5;

    ctx.beginPath();
    ctx.moveTo(p1.x - ex, p1.y);
    ctx.lineTo(p2.x + ex, p2.y);
    ctx.lineTo(p3.x + ex, p3.y);
    ctx.lineTo(p4.x - ex, p4.y);
    ctx.closePath();
    ctx.clip();

    ctx.setTransform(
      dx1 / sw,
      dy1 / sw,
      dx2 / sh,
      dy2 / sh,
      p1.x,
      p1.y
    );

    ctx.drawImage(img, sx, sy, sw, sh, 0, 0, sw + overlap, sh);
    ctx.restore();
  }

  function addLocalPrintTexture(ctx, width, height, alpha, step, intensity) {
    const noiseCanvas = document.createElement('canvas');
    noiseCanvas.width = width;
    noiseCanvas.height = height;

    const nctx = noiseCanvas.getContext('2d');

    for (let y = 0; y < height; y += step) {
      for (let x = 0; x < width; x += step) {
        const n = Math.floor(128 + (Math.random() - 0.5) * intensity * 8);
        nctx.fillStyle = `rgb(${n}, ${n}, ${n})`;
        nctx.fillRect(x, y, step, step);
      }
    }

    ctx.save();
    ctx.globalAlpha = alpha;
    ctx.globalCompositeOperation = 'multiply';
    ctx.drawImage(noiseCanvas, 0, 0);
    ctx.restore();
  }

  function warpCanvasToQuad(sourceCanvas, width, height, quad, strips = 420, overlap = 1.25) {
    const out = document.createElement('canvas');
    out.width = width;
    out.height = height;

    const ctx = out.getContext('2d');
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';

    ensureFinalWarpHandles(quad);

    const srcStripW = width / strips;

    for (let i = 0; i < strips; i++) {
      const t0 = i / strips;
      const t1 = (i + 1) / strips;

      const top0 = cubicBezierPoint(quad.topLeft, quad.handles.topLeft, quad.handles.topRight, quad.topRight, t0);
      const top1 = cubicBezierPoint(quad.topLeft, quad.handles.topLeft, quad.handles.topRight, quad.topRight, t1);
      const bottom0 = cubicBezierPoint(quad.bottomLeft, quad.handles.bottomLeft, quad.handles.bottomRight, quad.bottomRight, t0);
      const bottom1 = cubicBezierPoint(quad.bottomLeft, quad.handles.bottomLeft, quad.handles.bottomRight, quad.bottomRight, t1);

      const sx = Math.floor(i * srcStripW);
      const nextSx = Math.ceil((i + 1) * srcStripW);
      const sw = Math.max(1, nextSx - sx);

      drawImageStripToQuad(
        ctx,
        sourceCanvas,
        sx,
        0,
        sw,
        height,
        top0,
        top1,
        bottom1,
        bottom0,
        overlap
      );
    }

    return out;
  }

  function createWarpedDesign(designImage, config) {
    const {
      trim,
      placement,
      warpOptions
    } = config;

    const {
      width = 360,
      height = 190
    } = placement;

    const {
      designScale = 1.2,
      designOffsetX = 0,
      designOffsetY = 0,
      topInsetLeft = 18,
      topInsetRight = 6,
      bottomInsetLeft = 6,
      bottomInsetRight = 18,
      topCurveLiftLeft = -18,
      topCurveLiftRight = -4,
      bottomCurveDropLeft = 8,
      bottomCurveDropRight = 16,
      leftSideBulge = 10,
      rightSideBulge = -6,
      verticalBendStrength = 0.16,
      shearX = 0.04,
      strips = 420,
      stripOverlap = 1.3,
      finalWarp = null
    } = warpOptions;

    const sourceDesign = getTrimmedDesignCanvas(designImage, trim);
    const preparedDesign = document.createElement('canvas');
    preparedDesign.width = width;
    preparedDesign.height = height;

    const pdCtx = preparedDesign.getContext('2d');
    pdCtx.imageSmoothingEnabled = true;
    pdCtx.imageSmoothingQuality = 'high';

    const fit = Math.min(width / sourceDesign.width, height / sourceDesign.height) * designScale;
    const drawW = sourceDesign.width * fit;
    const drawH = sourceDesign.height * fit;
    const drawX = (width - drawW) / 2 + designOffsetX;
    const drawY = (height - drawH) / 2 + designOffsetY;

    pdCtx.clearRect(0, 0, width, height);
    pdCtx.drawImage(sourceDesign, drawX, drawY, drawW, drawH);

    const warpedCanvas = document.createElement('canvas');
    warpedCanvas.width = width;
    warpedCanvas.height = height;

    const wctx = warpedCanvas.getContext('2d');
    wctx.imageSmoothingEnabled = true;
    wctx.imageSmoothingQuality = 'high';

    const topLeft = { x: topInsetLeft, y: 0 };
    const topRight = { x: width - topInsetRight, y: 0 };
    const bottomLeft = { x: bottomInsetLeft, y: height };
    const bottomRight = { x: width - bottomInsetRight, y: height };

    const topCurve = {
      cp1: { x: width * 0.26, y: topCurveLiftLeft },
      cp2: { x: width * 0.76, y: topCurveLiftRight }
    };

    const bottomCurve = {
      cp1: { x: width * 0.24, y: height + bottomCurveDropLeft },
      cp2: { x: width * 0.78, y: height + bottomCurveDropRight }
    };

    const srcStripW = preparedDesign.width / strips;

    for (let i = 0; i < strips; i++) {
      const t0 = i / strips;
      const t1 = (i + 1) / strips;

      const top0 = cubicBezierPoint(topLeft, topCurve.cp1, topCurve.cp2, topRight, t0);
      const top1 = cubicBezierPoint(topLeft, topCurve.cp1, topCurve.cp2, topRight, t1);
      const bottom0 = cubicBezierPoint(bottomLeft, bottomCurve.cp1, bottomCurve.cp2, bottomRight, t0);
      const bottom1 = cubicBezierPoint(bottomLeft, bottomCurve.cp1, bottomCurve.cp2, bottomRight, t1);

      const tm = (t0 + t1) * 0.5;
      const sideBulge = (1 - tm) * leftSideBulge + tm * rightSideBulge;
      const belly = Math.sin(tm * Math.PI);

      top0.y += belly * height * verticalBendStrength * 0.35;
      top1.y += belly * height * verticalBendStrength * 0.35;
      bottom0.y -= belly * height * verticalBendStrength * 0.18;
      bottom1.y -= belly * height * verticalBendStrength * 0.18;

      top0.x += sideBulge * belly;
      top1.x += sideBulge * belly;
      bottom0.x += sideBulge * belly * 0.9;
      bottom1.x += sideBulge * belly * 0.9;

      const shearOffset0 = (top0.y / height) * shearX * width * 0.2;
      const shearOffset1 = (top1.y / height) * shearX * width * 0.2;

      top0.x += shearOffset0;
      top1.x += shearOffset1;
      bottom0.x += shearOffset0 * 0.4;
      bottom1.x += shearOffset1 * 0.4;

      const sx = Math.floor(i * srcStripW);
      const nextSx = Math.ceil((i + 1) * srcStripW);
      const sw = Math.max(1, nextSx - sx);

      drawImageStripToQuad(
        wctx,
        preparedDesign,
        sx,
        0,
        sw,
        preparedDesign.height,
        top0,
        top1,
        bottom1,
        bottom0,
        stripOverlap
      );
    }

    addLocalPrintTexture(wctx, width, height, 0.025, 2, 5);

    if (finalWarp) {
      return warpCanvasToQuad(warpedCanvas, width, height, finalWarp, strips, stripOverlap);
    }

    return warpedCanvas;
  }

  function applySoftShadingOverlay(ctx, baseImage, placement, maskCanvas, multiplyAlpha = 0.1, screenAlpha = 0.03) {
    const shadeCanvas = document.createElement('canvas');
    shadeCanvas.width = ctx.canvas.width;
    shadeCanvas.height = ctx.canvas.height;

    const sctx = shadeCanvas.getContext('2d');
    sctx.drawImage(baseImage, 0, 0);

    const maskedShadeCanvas = document.createElement('canvas');
    maskedShadeCanvas.width = placement.width;
    maskedShadeCanvas.height = placement.height;

    const msctx = maskedShadeCanvas.getContext('2d');
    msctx.drawImage(
      shadeCanvas,
      placement.x,
      placement.y,
      placement.width,
      placement.height,
      0,
      0,
      placement.width,
      placement.height
    );
    msctx.globalCompositeOperation = 'destination-in';
    msctx.drawImage(maskCanvas, 0, 0, placement.width, placement.height);

    ctx.save();
    ctx.globalCompositeOperation = 'multiply';
    ctx.globalAlpha = multiplyAlpha;
    ctx.drawImage(maskedShadeCanvas, placement.x, placement.y, placement.width, placement.height);
    ctx.restore();

    ctx.save();
    ctx.globalCompositeOperation = 'screen';
    ctx.globalAlpha = screenAlpha;
    ctx.drawImage(maskedShadeCanvas, placement.x, placement.y, placement.width, placement.height);
    ctx.restore();
  }

  async function renderToCanvas({ canvasEl, baseSrc, designSrc, config }) {
    const [baseImage, designImage] = await Promise.all([
      loadImage(baseSrc),
      loadImage(designSrc)
    ]);

    const ctx = canvasEl.getContext('2d');
    canvasEl.width = baseImage.width;
    canvasEl.height = baseImage.height;

    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.clearRect(0, 0, canvasEl.width, canvasEl.height);
    ctx.drawImage(baseImage, 0, 0);

    const warpedDesign = createWarpedDesign(designImage, config);

    ctx.drawImage(
      warpedDesign,
      config.placement.x,
      config.placement.y,
      config.placement.width,
      config.placement.height
    );

    if (config.mirroredPlacement?.enabled) {
      const mirroredPlacement = {
        x: config.placement.x + (config.mirroredPlacement.offsetX || 0),
        y: config.placement.y + (config.mirroredPlacement.offsetY || 0),
        width: config.placement.width,
        height: config.placement.height
      };

      const mirroredWarpedDesign = createMirroredCanvas(warpedDesign);
      ctx.drawImage(
        mirroredWarpedDesign,
        mirroredPlacement.x,
        mirroredPlacement.y,
        mirroredPlacement.width,
        mirroredPlacement.height
      );

      applySoftShadingOverlay(
        ctx,
        baseImage,
        mirroredPlacement,
        mirroredWarpedDesign,
        config.shading.multiplyAlpha,
        config.shading.screenAlpha
      );
    }

    applySoftShadingOverlay(
      ctx,
      baseImage,
      config.placement,
      warpedDesign,
      config.shading.multiplyAlpha,
      config.shading.screenAlpha
    );
  }

  window.VanscapeSlippersMockup = {
    defaultConfig: {
      placement: {
        x: 10,
        y: 383,
        width: 650,
        height: 450
      },
      mirroredPlacement: {
        enabled: true,
        offsetX: 360,
        offsetY: 0
      },
      trim: {
        enabled: true,
        alphaThreshold: 24,
        padding: 2
      },
      warpOptions: {
        designScale: 0.4,
          designOffsetX: -10,
          designOffsetY: 10,
          designRotation: 0,
          topInsetLeft: 20,
          topInsetRight: 8,
          bottomInsetLeft: 8,
          bottomInsetRight: 22,
          topCurveLiftLeft: -24,
          topCurveLiftRight: -6,
          bottomCurveDropLeft: 10,
          bottomCurveDropRight: 18,
          leftSideBulge: 14,
          rightSideBulge: -8,
          verticalBendStrength: 0.18,
          shearX: 0.05,
          strips: 460,
          stripOverlap: 1.35,
        finalWarp: {
          topLeft:     { x: -19, y: -19 },
          topRight:    { x: 652, y: -33 },
          bottomRight: { x: 653, y: 481 },
          bottomLeft:  { x: 0, y: 450 },
          handles: {
            topLeft:     { x: 205, y: -2 },
            topRight:    { x: 487, y: -20 },
            bottomRight: { x: 444, y: 538 },
            bottomLeft:  { x: 212, y: 493 }
          }
        }
      },
      shading: {
        multiplyAlpha: 0.11,
        screenAlpha: 0.035
      }
    },
    renderToCanvas
  };
})();
